// Slutöverlämningsgrind: obligatorisk kontroll innan projektet lämnas över till
// byggherre/förvaltning. Överlämningen kan bara genomföras när alla obligatoriska
// handlingar finns – annars krävs en skriftlig motivering som loggas.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Lock, LockOpen, ShieldCheck } from "lucide-react";
import type { HandoverEntry } from "@/lib/project/finalDocumentation";
import { useCurrentPhase } from "@/hooks/useCurrentPhase";

/** Handlingar som måste finnas innan slutöverlämning kan genomföras. */
export const REQUIRED_HANDOVER_DOCS = ["12", "31", "33", "22", "34"];

interface Props {
  projectId: string;
  entries: HandoverEntry[];
  canEdit?: boolean;
}

interface HandoverRow {
  id: string;
  status: string;
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_role: string | null;
  notes: string | null;
  override_motive: string | null;
  handed_over_at: string | null;
  phase_key: string | null;
}

export const HandoverGate: React.FC<Props> = ({ projectId, entries, canEdit = true }) => {
  const [row, setRow] = useState<HandoverRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientRole, setRecipientRole] = useState("Förvaltare");
  const [motive, setMotive] = useState("");
  const phase = useCurrentPhase(projectId);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("project_handovers")
      .select(
        "id, status, recipient_name, recipient_email, recipient_role, notes, override_motive, handed_over_at, phase_key"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const r = (data as HandoverRow | null) ?? null;
    setRow(r);
    setRecipientName(r?.recipient_name ?? "");
    setRecipientEmail(r?.recipient_email ?? "");
    setRecipientRole(r?.recipient_role ?? "Förvaltare");
    setMotive(r?.override_motive ?? "");
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const required = useMemo(
    () => entries.filter((e) => REQUIRED_HANDOVER_DOCS.includes(e.docNumber)),
    [entries]
  );
  const missing = required.filter((e) => !e.ok);
  const gateOpen = missing.length === 0;
  const completed = row?.status === "genomford";

  const performHandover = async () => {
    if (!gateOpen && motive.trim().length < 10) {
      toast.error("Beskriv i minst tio tecken varför överlämningen görs trots luckor");
      return;
    }
    if (!recipientName.trim()) {
      toast.error("Ange vem som tar emot dokumentationen");
      return;
    }
    setSaving(true);
    const { data: auth } = await supabase.auth.getUser();
    const payload = {
      project_id: projectId,
      status: "genomford",
      recipient_name: recipientName.trim(),
      recipient_email: recipientEmail.trim() || null,
      recipient_role: recipientRole,
      override_motive: gateOpen ? null : motive.trim(),
      missing_json: missing.map((m) => ({ docNumber: m.docNumber, title: m.title })),
      checklist_json: Object.fromEntries(required.map((r) => [r.docNumber, r.ok])),
      phase_key: phase.key ?? null,
      handed_over_at: new Date().toISOString(),
      handed_over_by: auth.user?.id ?? null,
      created_by: auth.user?.id ?? null,
    };
    const { error } = row
      ? await supabase.from("project_handovers").update(payload).eq("id", row.id)
      : await supabase.from("project_handovers").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Kunde inte registrera överlämningen");
      return;
    }
    toast.success("Slutöverlämningen är registrerad");
    void load();
  };

  const reopen = async () => {
    if (!row) return;
    const { error } = await supabase
      .from("project_handovers")
      .update({ status: "utkast", handed_over_at: null })
      .eq("id", row.id);
    if (error) toast.error("Kunde inte öppna överlämningen igen");
    else void load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {completed ? (
            <ShieldCheck className="h-4 w-4 text-primary" />
          ) : gateOpen ? (
            <LockOpen className="h-4 w-4 text-primary" />
          ) : (
            <Lock className="h-4 w-4 text-muted-foreground" />
          )}
          Grind för slutöverlämning
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Laddar…</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Dessa handlingar måste finnas innan dokumentationen får lämnas över till byggherren
              eller förvaltaren.
            </p>
            <div className="space-y-2">
              {required.map((e) => (
                <div
                  key={e.docNumber}
                  className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm"
                >
                  <span>
                    Dok {e.docNumber} · {e.title}
                  </span>
                  <Badge variant={e.ok ? "default" : "destructive"}>
                    {e.ok ? "Finns" : "Saknas"}
                  </Badge>
                </div>
              ))}
            </div>

            {completed ? (
              <div className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3 text-sm">
                <p className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  Överlämnad {row?.handed_over_at?.slice(0, 10)} till {row?.recipient_name}
                  {row?.recipient_role ? ` (${row.recipient_role})` : ""}
                </p>
                {row?.override_motive && (
                  <p className="text-muted-foreground">
                    Genomförd trots luckor. Motivering: {row.override_motive}
                  </p>
                )}
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={reopen}>
                    Öppna för komplettering
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label>Mottagare</Label>
                    <Input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Namn"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>E-post</Label>
                    <Input
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="namn@foretag.se"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Roll</Label>
                    <Input
                      value={recipientRole}
                      onChange={(e) => setRecipientRole(e.target.value)}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                {!gateOpen && (
                  <div className="space-y-1">
                    <Label>Motivering – varför lämnas det över trots luckor?</Label>
                    <Textarea
                      rows={3}
                      value={motive}
                      onChange={(e) => setMotive(e.target.value)}
                      placeholder="Beskriv vad som saknas, varför och när det kompletteras."
                      disabled={!canEdit}
                    />
                  </div>
                )}
                <Button onClick={performHandover} disabled={!canEdit || saving}>
                  {saving ? "Registrerar…" : "Genomför slutöverlämning"}
                </Button>
                {!gateOpen && (
                  <p className="text-xs text-muted-foreground">
                    {missing.length} obligatorisk(a) handling(ar) saknas. Överlämningen loggas då som
                    genomförd med avvikelse.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HandoverGate;
